import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { usePlan } from '../context/PlanContext';
import { fetchWithOrg } from '../utils/api';
import AgentBillingBadge from '../components/AgentBillingBadge';
import AgentSubscriptionCard from '../components/AgentSubscriptionCard';
import AgentActivationModal from '../components/AgentActivationModal';
import ActivationBlocker from '../components/ActivationBlocker';
import VoiceTestMode from '../components/VoiceTestMode';
import UpgradeModal from '../components/UpgradeModal';
import ConnectAIModelModal from '../components/ConnectAIModelModal';
import DeploymentModal from '../components/DeploymentModal';
import RunAgentModal from '../components/RunAgentModal';
import BlueprintAgentBanner, { ROIValueBox, AIModelStatusIndicator } from '../components/BlueprintAgentBanner';
import UniversalVoiceWizard from '../components/UniversalVoiceWizard';

import {
  ArrowLeft,
  ArrowRight,
  Brain,
  Calendar,
  CaretDown,
  ChatCircleText,
  CheckCircle,
  CircleNotch,
  Clock,
  Envelope,
  FileText,
  GearSix,
  Headphones,
  Image,
  Lightning,
  LinkSimple,
  MagicWand,
  Microphone,
  MusicNote,
  PencilSimple,
  Phone,
  PhoneCall,
  PlugsConnected,
  Robot,
  ShareNetwork,
  Sparkle,
  Warning,
  WarningCircle,
  Play,
  FileXls
} from '@phosphor-icons/react';

// ═══════════════════════════════════════════════════════════
// INPUT TYPE CONFIG
// ═══════════════════════════════════════════════════════════
const INPUT_TYPE_CONFIG = {
  text: { label: 'Text', icon: ChatCircleText, color: 'blue' },
  audio: { label: 'Audio', icon: MusicNote, color: 'purple' },
  image: { label: 'Image', icon: Image, color: 'pink' },
  pdf: { label: 'PDF', icon: FileText, color: 'red' },
  docx: { label: 'Document', icon: FileText, color: 'blue' },
  xlsx: { label: 'Spreadsheet', icon: FileXls, color: 'green' },
  csv: { label: 'CSV', icon: FileXls, color: 'green' },
  json: { label: 'JSON', icon: FileText, color: 'yellow' },
  url: { label: 'URL', icon: LinkSimple, color: 'cyan' },
  webhook_event: { label: 'Webhook', icon: PlugsConnected, color: 'orange' },
  calendar_event: { label: 'Calendar', icon: Calendar, color: 'teal' },
  email_thread: { label: 'Email', icon: Envelope, color: 'indigo' },
};

const getInputTypeChips = (inputTypes, maxDisplay = 3) => {
  if (!inputTypes || inputTypes.length === 0) inputTypes = ['text'];
  const chips = inputTypes.slice(0, maxDisplay).map(type => {
    const config = INPUT_TYPE_CONFIG[type] || { label: type, icon: FileText, color: 'gray' };
    return { type, ...config };
  });
  const overflow = inputTypes.length > maxDisplay ? inputTypes.length - maxDisplay : 0;
  return { chips, overflow, allTypes: inputTypes };
};

// ═══════════════════════════════════════════════════════════
// AGENT TYPE HELPERS
// ═══════════════════════════════════════════════════════════
const isVoiceAgent = (agent) => {
  const voiceProfiles = ['live_voice_s2s_agent', 'voice_input_agent', 'voice_output_tts_agent'];
  return voiceProfiles.includes(agent?.capability_profile) || 
         agent?.voice_mode === 'live_s2s' ||
         agent?.is_voice_template === true;
};

const isTelephonyAgent = (agent) => {
  return agent?.capability_profile === 'live_voice_s2s_agent' || agent?.voice_mode === 'live_s2s';
};

const isMeetingAgent = (agent) => {
  return agent?.capability_profile === 'voice_input_agent' ||
         agent?.requires_live_meeting_integration === true ||
         agent?.wizard_type === 'meeting_integration';
};

const getWizardUrl = (agent) => {
  const agentId = agent?.id || agent?.agent_id;
  if (agent?.capability_profile === 'voice_input_agent' && 
      (agent?.requires_live_meeting_integration || agent?.wizard_type === 'meeting_integration')) {
    return `/partner-pro/ai-workforce/agents/${agentId}/meeting-wizard`;
  }
  return `/partner-pro/ai-workforce/agents/${agentId}/wizard`;
};

// Generate outcome-driven headline from agent data
const getOutcomeHeadline = (agent) => {
  if (!agent) return 'Worker';
  const name = (agent.name || '').toLowerCase();
  // Enquiry / Support
  if (name.includes('enquir') || name.includes('customer') || name.includes('support'))
    return 'Respond to every new enquiry instantly';
  // Leads / Sales
  if (name.includes('lead') || name.includes('sales') || name.includes('follow'))
    return 'Never miss a lead again';
  // Email
  if (name.includes('email') || name.includes('inbox'))
    return 'Handle every email without lifting a finger';
  // Booking / Scheduling
  if (name.includes('book') || name.includes('schedul') || name.includes('appoint'))
    return 'Turn enquiries into booked jobs automatically';
  // Invoice / Payment
  if (name.includes('invoice') || name.includes('billing') || name.includes('payment'))
    return 'Process invoices and get paid faster';
  // Review / Feedback
  if (name.includes('review') || name.includes('feedback'))
    return 'Build your reputation on autopilot';
  // Meeting / Notes
  if (name.includes('meeting') || name.includes('note') || name.includes('minute'))
    return 'Capture every meeting without taking notes';
  // Report / Analysis
  if (name.includes('report') || name.includes('analys') || name.includes('data'))
    return 'Get insights without crunching numbers';
  // Social / Content
  if (name.includes('social') || name.includes('post') || name.includes('content'))
    return 'Keep your content flowing automatically';
  // Onboarding / Welcome
  if (name.includes('onboard') || name.includes('welcome'))
    return 'Welcome every new customer automatically';
  // Contract / Document
  if (name.includes('contract') || name.includes('document') || name.includes('summary'))
    return 'Process documents in seconds, not hours';
  // Quote / Proposal
  if (name.includes('quote') || name.includes('proposal'))
    return 'Send quotes before your competitors do';
  return agent.name || 'Worker';
};

// Generate contextual first-action prompt
const getFirstActionPrompt = (agent) => {
  if (!agent) return { cta: 'Try with a real example', placeholder: 'Type something to test...' };
  const name = (agent.name || '').toLowerCase();
  if (name.includes('enquir') || name.includes('customer') || name.includes('support'))
    return { cta: 'Try this with a real enquiry', placeholder: 'Paste a customer enquiry to test...' };
  if (name.includes('lead') || name.includes('sales'))
    return { cta: 'Simulate a new lead', placeholder: 'Describe a lead scenario to test...' };
  if (name.includes('email') || name.includes('inbox'))
    return { cta: 'Paste an email to test', placeholder: 'Paste an email to see how this worker responds...' };
  if (name.includes('review'))
    return { cta: 'Test with a sample job', placeholder: 'Describe a completed job to test...' };
  if (name.includes('report') || name.includes('analys'))
    return { cta: 'Try with sample data', placeholder: 'Paste some data to analyze...' };
  return { cta: 'Try with a real example', placeholder: 'Type something to test...' };
};

// Markdown renderers
const mdComponents = {
  p: ({children}) => <p className="text-[#B0B8C4] text-sm leading-relaxed mb-2">{children}</p>,
  strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
  ul: ({children}) => <ul className="list-disc list-inside ml-2 space-y-1">{children}</ul>,
  li: ({children}) => <li className="text-[#B0B8C4] text-sm">{children}</li>,
};

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const AgentDetail = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeOrganization } = useOrganization();
  const { userPlan } = usePlan();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [showVoiceTest, setShowVoiceTest] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [orgLLMConfig, setOrgLLMConfig] = useState({ has_key: false, llm_provider: null, default_model: null });
  const [showConnectAIModal, setShowConnectAIModal] = useState(false);
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [pendingActivation, setPendingActivation] = useState(false);
  const [linkedOpportunity, setLinkedOpportunity] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showWizardModal, setShowWizardModal] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const isBasicPlan = !userPlan || userPlan === 'basic' || userPlan === 'starter';
  const isBlueprintAgent = agent?.source_opportunity_id || agent?.source === 'blueprint_generator' || agent?.from_blueprint === true;
  const isActive = agent?.status === 'active' || agent?.billing_status === 'active';
  const hasRuns = recentSessions.length > 0;

  // ═══════ FETCH ORG LLM CONFIG ═══════
  const fetchOrgLLMConfig = useCallback(async () => {
    if (!activeOrganization?.organization_id) return;
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/llm-config`,
        { credentials: 'include' },
        activeOrganization.organization_id
      );
      if (response.ok) {
        const data = await response.json();
        setOrgLLMConfig({ has_key: data.has_key || false, llm_provider: data.llm_provider || null, default_model: data.default_model || null });
      }
    } catch (err) { console.error('Error fetching org LLM config:', err); }
  }, [activeOrganization?.organization_id]);

  // ═══════ ACTIVATION LOGIC ═══════
  const handleActivateClick = () => {
    if (isBasicPlan) { setShowUpgradeModal(true); return; }
    if (isTelephonyAgent(agent) && !agent.wizard_completed) {
      toast.warning('Please complete the setup wizard before activating.');
      navigate(getWizardUrl(agent));
      return;
    }
    if (agent?.use_org_llm_key !== false && !orgLLMConfig.has_key) {
      setPendingActivation(true);
      setShowConnectAIModal(true);
      return;
    }
    setShowActivationModal(true);
  };

  const handleLLMKeyConnected = async () => {
    await fetchOrgLLMConfig();
    setShowConnectAIModal(false);
    if (pendingActivation) {
      setPendingActivation(false);
      setShowActivationModal(true);
    }
  };

  // ═══════ PAYMENT VERIFICATION ═══════
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const purchaseType = searchParams.get('type');
    if (paymentStatus === 'success' && sessionId && purchaseType === 'agent') {
      verifyAgentPayment(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled');
      setSearchParams({});
    }
  }, [searchParams]);

  const verifyAgentPayment = async (sessionId) => {
    setVerifyingPayment(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/stripe/verify-agent-payment?session_id=${sessionId}`, {
        headers: { 'X-Organization-Id': activeOrganization?.organization_id },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'paid' || data.status === 'complete') {
          toast.success('Worker activated successfully!');
          await fetchAgent();
        } else { toast.info('Payment is being processed...'); }
      } else { toast.error('Could not verify payment. Please refresh.'); }
    } catch { toast.error('Error verifying payment'); }
    finally { setVerifyingPayment(false); setSearchParams({}); }
  };

  // ═══════ FETCH AGENT ═══════
  useEffect(() => {
    if (activeOrganization) { fetchAgent(); fetchOrgLLMConfig(); }
  }, [agentId, activeOrganization, fetchOrgLLMConfig]);

  useEffect(() => {
    const fetchLinkedOpportunity = async () => {
      if (!agent?.source_opportunity_id || !activeOrganization?.organization_id) return;
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/opportunities/${agent.source_opportunity_id}`,
          { credentials: 'include' }, activeOrganization.organization_id
        );
        if (response.ok) setLinkedOpportunity(await response.json());
      } catch {}
    };
    fetchLinkedOpportunity();
  }, [agent?.source_opportunity_id, activeOrganization?.organization_id]);

  useEffect(() => {
    const fetchRecentSessions = async () => {
      const id = agent?.id || agent?.agent_id;
      if (!id || !activeOrganization?.organization_id) return;
      setLoadingSessions(true);
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/my/sessions?agent_id=${id}&limit=5`, {},
          activeOrganization.organization_id
        );
        if (response.ok) {
          const data = await response.json();
          setRecentSessions(data.sessions || []);
        }
      } catch {}
      finally { setLoadingSessions(false); }
    };
    fetchRecentSessions();
  }, [agent?.id, agent?.agent_id, activeOrganization?.organization_id]);

  const fetchAgent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/agents/${agentId}`, {
        headers: { 'X-Organization-Id': activeOrganization?.organization_id },
        credentials: 'include'
      });
      if (response.ok) setAgent(await response.json());
      else navigate('/automations');
    } catch { navigate('/automations'); }
    finally { setLoading(false); }
  };

  const refreshSessions = () => {
    const id = agent?.id || agent?.agent_id;
    if (id) {
      fetchWithOrg(`${BACKEND_URL}/api/my/sessions?agent_id=${id}&limit=5`, {}, activeOrganization.organization_id)
        .then(res => res.ok ? res.json() : null)
        .then(data => { if (data) setRecentSessions(data.sessions || []); })
        .catch(() => {});
    }
  };

  // ═══════ TIME FORMATTING ═══════
  const timeAgo = (ts) => {
    if (!ts) return '';
    const dateStr = typeof ts === 'string' && !ts.endsWith('Z') && !ts.includes('+') ? ts + 'Z' : ts;
    const d = new Date(dateStr);
    const secs = Math.floor((Date.now() - d) / 1000);
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // ═══════ LOADING & ERROR ═══════
  if (loading) {
    return (
      <div className="container mx-auto flex items-center justify-center min-h-[60vh]">
        <CircleNotch size={48} className="animate-spin text-[#00E5C0]" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="container mx-auto px-6 pt-12 text-center">
        <p className="text-white text-lg mb-4">Worker not found</p>
        <button onClick={() => navigate('/automations')} className="text-[#00E5C0] text-sm hover:underline">
          <ArrowLeft size={14} className="inline mr-1" /> Back to Workers
        </button>
      </div>
    );
  }

  const headline = getOutcomeHeadline(agent);
  const { cta: firstActionCta, placeholder: firstActionPlaceholder } = getFirstActionPrompt(agent);

  return (
    <>
      <div className="container mx-auto max-w-4xl">
        {/* Back */}
        <button
          onClick={() => navigate('/automations')}
          className="flex items-center gap-2 text-[#B0B8C4] hover:text-white mb-6 transition-colors text-sm"
          data-testid="back-to-workers"
        >
          <ArrowLeft size={16} /> Back to Workers
        </button>

        {/* Blueprint Banner */}
        {isBlueprintAgent && (
          <BlueprintAgentBanner
            opportunityData={linkedOpportunity}
            onCustomize={() => {
              if (isVoiceAgent(agent)) navigate(getWizardUrl(agent));
              else navigate(`/partner-pro/ai-workforce/agents/${agent.id || agent.agent_id}/edit`);
            }}
          />
        )}

        {/* ═══════════════════════════════════════════════════════
            HEADER — Outcome headline + status + primary action
            ═══════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-6 mb-5"
          data-testid="worker-header"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-[rgba(0,229,192,0.1)] flex items-center justify-center flex-shrink-0">
                  <Robot size={20} weight="duotone" className="text-[#00E5C0]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white" data-testid="worker-headline">
                    {headline}
                  </h1>
                  <p className="text-xs text-[#7D8896] mt-0.5">{agent.name}</p>
                </div>
              </div>

              {/* Status + badges row */}
              <div className="flex items-center gap-2 flex-wrap mt-3">
                <span
                  data-testid="worker-status-badge"
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                    isActive
                      ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border border-[rgba(0,229,192,0.2)]'
                      : 'bg-[rgba(255,255,255,0.04)] text-[#7D8896] border border-[rgba(255,255,255,0.08)]'
                  }`}
                >
                  {isActive ? 'Running' : agent.status === 'paused' ? 'Paused' : 'Draft'}
                </span>
                <AgentBillingBadge billingStatus={agent.billing_status} isActive={isActive} compact={true} />
              </div>
            </div>

            {/* Primary action buttons */}
            <div className="flex items-center gap-2 shrink-0">
              {!isActive && (
                <button
                  onClick={handleActivateClick}
                  data-testid="activate-worker-btn"
                  className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0B0F14] text-sm font-semibold transition-colors"
                >
                  <Lightning size={16} weight="fill" /> Activate
                </button>
              )}
              {isActive && !isVoiceAgent(agent) && (
                <button
                  onClick={() => setShowRunModal(true)}
                  data-testid="run-worker-btn"
                  className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0B0F14] text-sm font-semibold transition-colors"
                >
                  <Play size={16} weight="fill" /> Run Worker
                </button>
              )}
              {isTelephonyAgent(agent) && (
                <button
                  onClick={() => setShowVoiceTest(true)}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[rgba(139,92,246,0.1)] text-[#A78BFA] text-sm font-medium border border-[rgba(139,92,246,0.2)] hover:bg-[rgba(139,92,246,0.15)] transition-colors"
                >
                  <PhoneCall size={16} /> Test Voice
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════
            WHAT THIS WORKER DOES
            ═══════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-6 mb-5"
          data-testid="worker-description"
        >
          <h2 className="text-xs font-semibold text-[#7D8896] uppercase tracking-wider mb-3">What this worker does</h2>
          <div className="prose prose-invert prose-sm max-w-none">
            <ReactMarkdown components={mdComponents}>
              {agent.description || 'This worker automates a task in your business.'}
            </ReactMarkdown>
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════
            FIRST ACTION PROMPT — prominent, centered, unavoidable
            Only shown for workers that haven't been run yet
            ═══════════════════════════════════════════════════════ */}
        {isActive && !hasRuns && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl border-2 border-[rgba(0,229,192,0.25)] bg-[rgba(0,229,192,0.04)] p-8 mb-5 text-center"
            data-testid="first-action-prompt"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(0,229,192,0.1)] text-[#00E5C0] text-xs font-semibold mb-4">
              <Sparkle size={14} weight="fill" /> This is now running for your business
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{firstActionCta}</h2>
            <p className="text-sm text-[#B0B8C4] mb-6 max-w-md mx-auto">
              See how this worker handles a real scenario. No setup needed — just test it.
            </p>
            <button
              onClick={() => setShowRunModal(true)}
              data-testid="first-action-run-btn"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0B0F14] text-base font-bold transition-colors shadow-lg shadow-[rgba(0,229,192,0.15)]"
            >
              <Play size={20} weight="fill" /> {firstActionCta}
            </button>
            <p className="text-xs text-[#7D8896] mt-4">
              This reply would have taken ~5 minutes manually
            </p>
          </motion.div>
        )}

        {/* ═══════════════════════════════════════════════════════
            FIRST ACTION PROMPT — for inactive workers
            ═══════════════════════════════════════════════════════ */}
        {!isActive && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl border border-dashed border-[rgba(0,229,192,0.2)] bg-[rgba(0,229,192,0.02)] p-8 mb-5 text-center"
            data-testid="activate-prompt"
          >
            <Lightning size={32} weight="duotone" className="text-[#00E5C0] mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white mb-2">Activate this worker to get started</h2>
            <p className="text-sm text-[#B0B8C4] mb-6 max-w-md mx-auto">
              Once activated, you can test it with a real example and see results instantly.
            </p>
            <button
              onClick={handleActivateClick}
              data-testid="activate-prompt-btn"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0B0F14] text-base font-bold transition-colors"
            >
              <Lightning size={20} weight="fill" /> Activate Worker
            </button>
            {/* AI Model Status */}
            <div className="mt-4">
              <AIModelStatusIndicator hasKey={orgLLMConfig.has_key} provider={orgLLMConfig.llm_provider} />
            </div>
          </motion.div>
        )}

        {/* ═══════════════════════════════════════════════════════
            ACTIVITY — Recent runs (compact)
            ═══════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-6 mb-5"
          data-testid="worker-activity"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-[#7D8896] uppercase tracking-wider flex items-center gap-2">
              <Clock size={14} /> Activity
            </h2>
            {hasRuns && (
              <button
                onClick={() => navigate('/inbox')}
                className="text-[10px] text-[#00E5C0] hover:underline flex items-center gap-1"
                data-testid="view-all-activity"
              >
                View All <ArrowRight size={10} />
              </button>
            )}
          </div>

          {loadingSessions ? (
            <div className="flex justify-center py-6">
              <CircleNotch size={20} className="animate-spin text-[#00E5C0]" />
            </div>
          ) : !hasRuns ? (
            <div className="text-center py-6">
              <p className="text-sm text-[#7D8896]">No runs yet. {isActive ? 'Run this worker to see results here.' : 'Activate this worker to get started.'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentSessions.map((session) => {
                const statusColors = {
                  completed: 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border-[rgba(0,229,192,0.2)]',
                  running: 'bg-[rgba(59,130,246,0.1)] text-[#3B82F6] border-[rgba(59,130,246,0.2)]',
                  failed: 'bg-[rgba(239,68,68,0.1)] text-[#EF4444] border-[rgba(239,68,68,0.2)]',
                  queued: 'bg-[rgba(255,255,255,0.04)] text-[#7D8896] border-[rgba(255,255,255,0.08)]'
                };
                const sc = statusColors[session.status] || statusColors.queued;
                return (
                  <button
                    key={session.session_id}
                    onClick={() => navigate(`/partner-pro/workspace/sessions/${session.session_id}`)}
                    className="w-full text-left p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(0,229,192,0.2)] transition-all"
                    data-testid={`run-${session.session_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded border font-medium ${sc}`}>
                        {session.status}
                      </span>
                      <span className="text-[10px] text-[#7D8896]">{timeAgo(session.created_at)}</span>
                    </div>
                    {session.input_payload?.text && (
                      <p className="text-xs text-[#B0B8C4] mt-1.5 truncate">
                        {session.input_payload.text.slice(0, 80)}{session.input_payload.text.length > 80 ? '...' : ''}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* ═══════════════════════════════════════════════════════
            TOOLS — Connected integrations with status + Connect CTA
            ═══════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-6 mb-5"
          data-testid="worker-tools"
        >
          <h2 className="text-xs font-semibold text-[#7D8896] uppercase tracking-wider flex items-center gap-2 mb-4">
            <PlugsConnected size={14} /> Tools
          </h2>

          <div className="space-y-3">
            {/* AI Model */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[rgba(139,92,246,0.1)] flex items-center justify-center">
                  <Brain size={18} className="text-[#A78BFA]" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">AI Model</p>
                  <p className="text-[10px] text-[#7D8896]">
                    {orgLLMConfig.has_key ? `${orgLLMConfig.llm_provider || 'Connected'} — ${orgLLMConfig.default_model || 'Default'}` : 'Required for this worker to run'}
                  </p>
                </div>
              </div>
              {orgLLMConfig.has_key ? (
                <span className="text-[10px] px-2 py-1 rounded-full bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] font-medium" data-testid="tool-ai-connected">Connected</span>
              ) : (
                <button
                  onClick={() => setShowConnectAIModal(true)}
                  className="text-xs px-3 py-1.5 rounded-lg bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.15)] transition-colors font-medium"
                  data-testid="tool-ai-connect-btn"
                >
                  Connect
                </button>
              )}
            </div>

            {/* Meeting Platforms — for meeting agents */}
            {isMeetingAgent(agent) && (
              <div className="flex items-center justify-between p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[rgba(139,92,246,0.1)] flex items-center justify-center">
                    <Headphones size={18} className="text-[#8B5CF6]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Meeting Platforms</p>
                    <p className="text-[10px] text-[#7D8896]">Connect Teams, Meet, or Zoom to capture meetings automatically</p>
                  </div>
                </div>
                {agent.meeting_config?.connected_platforms && Object.keys(agent.meeting_config.connected_platforms).length > 0 ? (
                  <span className="text-[10px] px-2 py-1 rounded-full bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] font-medium">Connected</span>
                ) : (
                  <button
                    onClick={() => navigate(getWizardUrl(agent))}
                    className="text-xs px-3 py-1.5 rounded-lg bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.15)] transition-colors font-medium"
                  >
                    Connect
                  </button>
                )}
              </div>
            )}

            {/* Telephony — for voice agents */}
            {isTelephonyAgent(agent) && (
              <div className="flex items-center justify-between p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[rgba(239,68,68,0.1)] flex items-center justify-center">
                    <Phone size={18} className="text-[#FF6B6B]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Telephony</p>
                    <p className="text-[10px] text-[#7D8896]">
                      {agent.installation_config?.telephony?.provider === 'telnyx' ? 'Telnyx' : 'Twilio'} for voice calls
                    </p>
                  </div>
                </div>
                {agent.installation_config?.telephony?.validated && agent.installation_config?.telephony?.phone_number ? (
                  <span className="text-[10px] px-2 py-1 rounded-full bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] font-medium">Connected</span>
                ) : (
                  <button
                    onClick={() => navigate(getWizardUrl(agent))}
                    className="text-xs px-3 py-1.5 rounded-lg bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.15)] transition-colors font-medium"
                  >
                    Connect
                  </button>
                )}
              </div>
            )}

            {/* Calendar */}
            {(agent.calendar_config?.enabled || agent.agent_config?.is_booking_agent) && !isMeetingAgent(agent) && (
              <div className="flex items-center justify-between p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[rgba(59,130,246,0.1)] flex items-center justify-center">
                    <Calendar size={18} className="text-[#3B82F6]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Calendar</p>
                    <p className="text-[10px] text-[#7D8896]">
                      {agent.calendar_config?.provider === 'microsoft' ? 'Microsoft Calendar' : 'Google Calendar'}
                    </p>
                  </div>
                </div>
                {agent.calendar_config?.enabled ? (
                  <span className="text-[10px] px-2 py-1 rounded-full bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] font-medium">Connected</span>
                ) : (
                  <button
                    onClick={() => navigate(getWizardUrl(agent))}
                    className="text-xs px-3 py-1.5 rounded-lg bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.15)] transition-colors font-medium"
                  >
                    Connect
                  </button>
                )}
              </div>
            )}

            {/* Inline Connection CTA after run */}
            {isActive && hasRuns && (
              <div className="mt-3 p-4 rounded-xl bg-[rgba(0,229,192,0.03)] border border-dashed border-[rgba(0,229,192,0.15)]" data-testid="connection-cta">
                <p className="text-sm text-[#B0B8C4]">
                  <PlugsConnected size={14} className="inline text-[#00E5C0] mr-1.5" />
                  Connect your tools to start handling real work automatically.{' '}
                  <button
                    onClick={() => navigate('/apps')}
                    className="text-[#00E5C0] hover:underline font-medium"
                  >
                    Browse integrations &rarr;
                  </button>
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════
            ADVANCED — Collapsible, all technical details
            ═══════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] mb-5"
          data-testid="worker-advanced"
        >
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between p-5 text-left"
            data-testid="advanced-toggle"
          >
            <div className="flex items-center gap-2">
              <GearSix size={14} className="text-[#7D8896]" />
              <span className="text-xs font-semibold text-[#7D8896] uppercase tracking-wider">Advanced</span>
            </div>
            <CaretDown
              size={14}
              className={`text-[#7D8896] transition-transform duration-200 ${showAdvanced ? '' : '-rotate-90'}`}
            />
          </button>

          {showAdvanced && (
            <div className="px-6 pb-6 space-y-5 border-t border-[rgba(255,255,255,0.04)] pt-5">
              {/* Configuration */}
              <div>
                <h3 className="text-xs text-[#7D8896] uppercase tracking-wider mb-2">Configuration</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] text-[#7D8896] block mb-1">Role</label>
                    <p className="text-white text-sm">{agent.role || 'Not specified'}</p>
                  </div>
                  {agent.capabilities?.length > 0 && (
                    <div>
                      <label className="text-[10px] text-[#7D8896] block mb-1">Capabilities</label>
                      <div className="flex flex-wrap gap-1.5">
                        {agent.capabilities.map((cap, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs">{cap}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {agent.linked_task_descriptions?.length > 0 && (
                    <div>
                      <label className="text-[10px] text-[#7D8896] block mb-1">Linked Tasks</label>
                      <ul className="space-y-1">
                        {agent.linked_task_descriptions.map((task, i) => (
                          <li key={i} className="text-white text-xs flex items-start gap-1.5">
                            <CheckCircle size={12} className="text-[#00E5C0] shrink-0 mt-0.5" weight="duotone" />
                            {task}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
                  <p className="text-[10px] text-[#7D8896] mb-1">LLM Provider</p>
                  <p className="text-white text-sm font-medium capitalize">{agent.llm_provider || 'Not set'}</p>
                </div>
                <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
                  <p className="text-[10px] text-[#7D8896] mb-1">Capabilities</p>
                  <p className="text-white text-sm font-medium">{agent.capabilities?.length || 0} active</p>
                </div>
                <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
                  <p className="text-[10px] text-[#7D8896] mb-1">Tasks</p>
                  <p className="text-white text-sm font-medium">{agent.linked_task_descriptions?.length || 0} linked</p>
                </div>
                <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
                  <p className="text-[10px] text-[#7D8896] mb-1">Created</p>
                  <p className="text-white text-sm font-medium">{new Date(agent.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Billing / Subscription */}
              {(isActive || agent.billing_status) && (
                <AgentSubscriptionCard agent={agent} onUpdate={fetchAgent} />
              )}

              {/* Blueprint ROI */}
              {isBlueprintAgent && linkedOpportunity?.roi && (
                <ROIValueBox annualSavings={linkedOpportunity.roi.annual_savings || linkedOpportunity.roi.roi_annual} />
              )}

              {/* Edit + Deploy actions */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => {
                    if (isVoiceAgent(agent)) setShowWizardModal(true);
                    else navigate(`/partner-pro/ai-workforce/agents/${agent.id || agent.agent_id}/edit`);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[#F0F4F8] text-xs font-medium hover:bg-[rgba(255,255,255,0.08)] transition-colors"
                  data-testid="edit-worker-btn"
                >
                  <PencilSimple size={14} /> Edit Configuration
                </button>
                {isActive && (
                  <button
                    onClick={() => setShowDeploymentModal(true)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs font-medium hover:bg-[rgba(255,255,255,0.08)] transition-colors"
                    data-testid="deploy-worker-btn"
                  >
                    <ShareNetwork size={14} /> Deploy & Share
                  </button>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* ═══════ MODALS ═══════ */}
      {showActivationModal && (
        <AgentActivationModal
          agent={agent}
          onClose={() => setShowActivationModal(false)}
          onSuccess={() => { setShowActivationModal(false); fetchAgent(); }}
        />
      )}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={userPlan || 'basic'}
        feature="Worker Deployment"
        message="Deploy AI workers to automate your workflows. Upgrade to Pro or Partner Pro to activate and run workers."
      />

      {showVoiceTest && agent && (
        <VoiceTestMode
          agentId={agent.id || agent.agent_id}
          agentName={agent.name}
          businessName={agent.agent_config?.context?.business_name || agent.name}
          businessDescription={agent.agent_config?.context?.business_description || agent.description}
          customKnowledge={agent.installation_config?.custom_knowledge || ''}
          openingMessage={agent.voice_config?.call_greeting || agent.agent_config?.opening_message || ''}
          requiredBookingInfo={agent.calendar_config?.required_booking_info || []}
          onClose={() => setShowVoiceTest(false)}
        />
      )}

      <ConnectAIModelModal
        isOpen={showConnectAIModal}
        onClose={() => { setShowConnectAIModal(false); setPendingActivation(false); }}
        onSuccess={handleLLMKeyConnected}
      />

      {agent && (
        <DeploymentModal
          agentId={agent.agent_id || agent.id}
          agentName={agent.name}
          isOpen={showDeploymentModal}
          onClose={() => setShowDeploymentModal(false)}
        />
      )}

      {agent && (
        <RunAgentModal
          agent={{ ...agent, id: agent.agent_id || agent.id }}
          isOpen={showRunModal}
          onClose={() => setShowRunModal(false)}
          onSuccess={(sessionId) => { console.log('Session completed:', sessionId); refreshSessions(); }}
        />
      )}

      {/* Voice Wizard Modal (Edit Configuration) */}
      {showWizardModal && (
        <UniversalVoiceWizard
          agentIdProp={agentId}
          isModal={true}
          onClose={() => {
            setShowWizardModal(false);
            fetchAgent();
          }}
        />
      )}
    </>
  );
};

export default AgentDetail;
